// src/homecomponents/Product.js

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

const translations = {
  es: {
    dynamicTitle: 'Una plataforma útil para',
    rotatingTexts: [
      'el empleado',
      'el líder',
      'el Director de Talento',
      'todos',
    ],
    buttons: ['Líder', 'Empleado', 'Director de Talento'],
  },
  en: {
    dynamicTitle: 'A useful platform for',
    rotatingTexts: [
      'the employee',
      'the leader',
      'the Talent Director',
      'everyone',
    ],
    buttons: ['Leader', 'Employee', 'Talent Director'],
  },
};

const importAll = (r) => r.keys().map(r);

// Carga dinámica de imágenes desde screenshots/ProductCarrousel
const employeeImages = importAll(
  require.context(
    './screenshots/ProductCarrousel/employee',
    false,
    /\.(png|jpe?g|svg)$/
  )
);
const managerImages = importAll(
  require.context(
    './screenshots/ProductCarrousel/manager',
    false,
    /\.(png|jpe?g|svg)$/
  )
);
const directorImages = importAll(
  require.context(
    './screenshots/ProductCarrousel/director',
    false,
    /\.(png|jpe?g|svg)$/
  )
);

const imagesByTab = {
  Empleado: employeeImages,
  Employee: employeeImages,
  Líder: managerImages,
  Leader: managerImages,
  'Director de Talento': directorImages,
  'Talent Director': directorImages,
};

function Product({ language }) {
  const { dynamicTitle, rotatingTexts, buttons } = translations[language];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(buttons[0]);
  const [imageIndex, setImageIndex] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 600);

  useEffect(() => {
    setImageIndex(0);
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(buttons[0]);
  }, [language, buttons]);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isSmallScreen) {
      const delay =
        rotatingTexts[currentIndex] ===
        (language === 'es' ? 'todos' : 'everyone')
          ? 6000
          : 4000;
      const t = setTimeout(
        () => setCurrentIndex((i) => (i + 1) % rotatingTexts.length),
        delay
      );
      return () => clearTimeout(t);
    }
  }, [currentIndex, rotatingTexts, isSmallScreen, language]);

  const tabTitles = {
    Líder: 'Tu Aliado en Gestión de Talento',
    Empleado: 'Tu Equipo en Modo Superhéroe',
    'Director de Talento': 'Todo el Talento Bajo Control',
    Leader: 'Your Partner in Talent Management',
    Employee: 'Your Team in Superhero Mode',
    'Talent Director': 'All Talent Under Control',
  };
  const tabContent = {
    Empleado: [
      'Identificar sus propias oportunidades de crecimiento',
      'Aprender e implementar nuevas acciones que se convierten en hábitos',
      'Cocrear y asumir la responsabilidad de sus planes de desarrollo',
    ],
    Employee: [
      'Identify their own growing opportunities',
      'Learn & implement new actions that became habits',
      'Cocreate and own their development plans',
    ],
    Líder: [
      'Analizar el equipo para detectar las oportunidades de desarrollo.',
      'Asegurar la continuidad del negocio con alertas de riesgo de rotación.',
      'Herramientas de talento AI que te proponen el siguiente paso para cada talento.',
    ],
    Leader: [
      'Analyse the team to detect talent development focus.',
      'Ensure business continuity with turnover risk warnings.',
      'AI-Talent tool that proposes next step for each talent.',
    ],
    'Director de Talento': [
      'Anticipar y mitigar los riesgos de rotación en el negocio.',
      'Asegurar que las estrategias de sucesión y sostenibilidad empresarial estén implementadas.',
      'Crear campañas de talento para detectar oportunidades de crecimiento.',
    ],
    'Talent Director': [
      'Anticipate and mitigate turnover business risks.',
      'Ensure succession and business sustainability strategies are placed.',
      'Create talent campaigns to grow.',
    ],
  };

  const images = imagesByTab[activeTab];
  const prev = () =>
    setImageIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setImageIndex((i) => (i + 1) % images.length);
  const goTo = (i) => setImageIndex(i);

  return (
    <section id="product" style={styles.section}>
      <div style={styles.contentBox}>
        <p style={styles.dynamicTitle}>
          {dynamicTitle}
          {isSmallScreen ? (
            <span style={styles.rotatingText}>
              {language === 'es' ? 'todos' : 'everyone'}
            </span>
          ) : (
            <AnimatePresence mode="wait">
              <motion.span
                key={currentIndex}
                style={styles.rotatingText}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.5 }}
              >
                {rotatingTexts[currentIndex]}
              </motion.span>
            </AnimatePresence>
          )}
        </p>

        <div style={styles.buttonContainer}>
          {buttons.map((b) => (
            <button
              key={b}
              onClick={() => setActiveTab(b)}
              style={activeTab === b ? styles.activeButton : styles.button}
            >
              {b}
            </button>
          ))}
        </div>

        <div style={styles.tabContent}>
          <div style={styles.imageWrapper}>
            <img
              src={images[imageIndex]}
              alt={`${activeTab} ${imageIndex + 1}`}
              style={styles.image}
            />
            <button onClick={prev} style={styles.arrowLeft}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button onClick={next} style={styles.arrowRight}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <div style={styles.dotsContainer}>
              {images.map((_, idx) => (
                <span
                  key={idx}
                  onClick={() => goTo(idx)}
                  style={idx === imageIndex ? styles.activeDot : styles.dot}
                />
              ))}
            </div>
          </div>

          <div style={styles.textContentCentered}>
            <h1 style={styles.title}>{tabTitles[activeTab]}</h1>
            <ul style={styles.list}>
              {tabContent[activeTab].map((item, i) => (
                <li key={i} style={styles.listItem}>
                  <FontAwesomeIcon icon={faCircleCheck} style={styles.icon} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

const styles = {
  section: {
    width: '100%',
    minHeight: '70vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    margin: 0,
    padding: '10px',
    boxSizing: 'border-box',
    position: 'relative',
  },
  contentBox: {
    width: '100%',
    maxWidth: 'calc(100% - 40px)',
    border: '1px solid #ddd',
    borderRadius: '20px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    padding: '40px',
    boxSizing: 'border-box',
    position: 'relative',
  },
  dynamicTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '20px',
    textAlign: 'left',
  },
  rotatingText: {
    marginLeft: '8px',
    color: '#ff2300',
    fontWeight: '700',
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    marginTop: '20px',
  },
  button: {
    padding: '10px 20px',
    borderRadius: '20px',
    border: 'none',
    backgroundColor: '#f0f0f0',
    fontSize: '1rem',
    fontWeight: '400',
    cursor: 'pointer',
    minWidth: '120px',
    textAlign: 'center',
    transition: 'background-color 0.3s, color 0.3s',
  },
  activeButton: {
    padding: '10px 20px',
    borderRadius: '20px',
    border: 'none',
    backgroundColor: '#d3d3d3',
    fontSize: '1rem',
    fontWeight: '400',
    cursor: 'pointer',
    color: '#333',
    minWidth: '120px',
    textAlign: 'center',
    transition: 'background-color 0.3s, color 0.3s',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '30px',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  textContentCentered: {
    flex: '1 1 300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    textAlign: 'left',
    padding: '20px',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '600px',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    marginBottom: '10px',
    textAlign: 'left',
  },
  list: {
    fontSize: '1.1rem',
    listStyle: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    lineHeight: '1.6',
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '8px',
    fontWeight: '300',
  },
  icon: {
    marginRight: '8px',
    color: '#ff2300',
    minWidth: '20px',
    marginTop: '4px',
  },
  imageWrapper: {
    position: 'relative',
    flex: '1 1 300px',
    width: '100%',
    aspectRatio: '16/9',
    overflow: 'hidden',
    borderRadius: '10px',
    backgroundColor: 'transparent', // fondo transparente en lugar de gris
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  arrowLeft: {
    position: 'absolute',
    top: '50%',
    left: '10px',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.8)',
    border: 'none',
    borderRadius: '50%',
    padding: '8px',
    cursor: 'pointer',
  },
  arrowRight: {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.8)',
    border: 'none',
    borderRadius: '50%',
    padding: '8px',
    cursor: 'pointer',
  },
  dotsContainer: {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ddd',
    cursor: 'pointer',
  },
  activeDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ff2300',
    cursor: 'pointer',
  },
};

export default Product;
